/**
 * B-24b rework (2026-06-08) — the single Operator-surface panel primitive.
 *
 * One chrome spec for the entire SA front door AND PlatformHealth, so the two
 * read as one app (HoD teardown P1-1). The canonical chrome is the old
 * OperatorQualityPanel card: rounded-lg + 1px default border + surface-card +
 * p-4, with a bold uppercase kicker over a sm semibold title.
 *
 * The panel is a flex column that fills its grid cell (`h-full w-full`) so
 * sibling panels in a row land at equal height (P0-4 fold-fill). The body slot
 * grows to consume the remaining height.
 */
import type { ReactNode } from "react";

interface Props {
  /** Uppercase eyebrow — human operator copy, never schema/jargon. */
  kicker: string;
  title: string;
  children: ReactNode;
  /** Optional right-aligned header slot (e.g. a count or status pill). */
  headerRight?: ReactNode;
  /** Optional test id forwarded to the section element. */
  testId?: string;
  /** Extra classes for the body wrapper (e.g. spacing overrides). */
  bodyClassName?: string;
}

export function OperatorPanel({
  kicker,
  title,
  children,
  headerRight,
  testId,
  bodyClassName = "",
}: Props): JSX.Element {
  return (
    <section
      data-testid={testId}
      className="flex h-full w-full flex-col rounded-lg border border-border-default bg-surface-card p-4"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-tertiary">
            {kicker}
          </p>
          <h3 className="mt-0.5 text-sm font-semibold text-text-primary">
            {title}
          </h3>
        </div>
        {headerRight}
      </div>
      <div className={"mt-3 flex-1 " + bodyClassName}>{children}</div>
    </section>
  );
}
